import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Drawer, Avatar, Text } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions } from 'actions';
import DrawerTabs from 'components/DrawerTabs';
import { FILTER_KEY as SUBSCRIBERS_COMPANIES_FILTER_KEY } from './CompaniesTableConfig';
import { FILTER_KEY as SUBSCRIBERS_CONTACTS_FILTER_KEY } from './ContactsTableConfig';
import { getLiteral } from 'utils/getLiteral';
import { intercomHideAndShow } from 'utils/intercom';
import { userflowHideAndShow } from 'utils/userflow';
import BulkMarketingSubscribedConfirmModal from '../bulkSubscribe/BulkMarketingSubscribedConfirmModal';
import useBulkMarketingSubscribedConfirm from '../bulkSubscribe/useBulkMarketingSubscribedConfirm';
import CompaniesTab from './CompaniesTab';
import ContactsTab from './ContactsTab';

import './styles.scss';

const TAB_COMPANIES = 'companies';
const TAB_CONTACTS = 'contacts';

const DRAWER_CONTENT_STYLES = { maxWidth: '85vw', height: '100%' };

const TabTitle = ({ icon, label }) => (
    <div className="fm-subscribers-panel__tab-title">
        <Avatar icon={icon} className="fm-subscribers-panel__tab-avatar" />
        <Text isTruncated>{label}</Text>
    </div>
);

const TAB_ENTITY_MAP = {
    [TAB_COMPANIES]: COMPANIES,
    [TAB_CONTACTS]: CONTACTS,
};

const Subscribers = memo(
    ({ onClose, setActiveEntity, resetListSelect, setShouldClearWeb5Checkboxes, hideFilters }) => {
        const {
            confirmState,
            handleOpenBulkSubscribedConfirm,
            handleCloseBulkSubscribedConfirm,
            handleConfirmBulkSubscribed,
        } = useBulkMarketingSubscribedConfirm({
            resetListSelect,
            setShouldClearWeb5Checkboxes,
        });

        useEffect(() => {
            userflowHideAndShow('hide');
            intercomHideAndShow('hide');
            hideFilters(COMPANIES, SUBSCRIBERS_COMPANIES_FILTER_KEY);
            hideFilters(CONTACTS, SUBSCRIBERS_CONTACTS_FILTER_KEY);
            return () => {
                userflowHideAndShow('show');
                intercomHideAndShow('show');
            };
        }, [hideFilters]);

        const handleTabChange = useCallback(
            (key) => {
                const entity = TAB_ENTITY_MAP[key];
                if (entity) setActiveEntity(entity);
            },
            [setActiveEntity],
        );

        const tabs = useMemo(
            () => [
                {
                    key: TAB_COMPANIES,
                    title: <TabTitle icon="accounts" label={getLiteral('label_accounts')} />,
                    content: (
                        <CompaniesTab
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
                {
                    key: TAB_CONTACTS,
                    title: <TabTitle icon="contacts" label={getLiteral('label_contacts')} />,
                    content: (
                        <ContactsTab
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
            ],
            [handleOpenBulkSubscribedConfirm],
        );

        return (
            <Drawer
                isOpen={true}
                onRequestClose={onClose}
                className="fm-subscribers-panel"
                width="1150px"
                contentStyles={DRAWER_CONTENT_STYLES}
                shouldCloseOnEsc={true}
                shouldCloseOnOverlayClick={false}
            >
                <DrawerTabs
                    className="fm-subscribers-panel__inner"
                    panelsClassName="fm-subscribers-panel__panels"
                    tabs={tabs}
                    defaultActiveTab={TAB_COMPANIES}
                    onClose={onClose}
                    onTabChange={handleTabChange}
                />
                <BulkMarketingSubscribedConfirmModal
                    isOpen={confirmState.isOpen}
                    isSubmitting={confirmState.isSubmitting}
                    payload={confirmState.payload}
                    onConfirm={handleConfirmBulkSubscribed}
                    onClose={handleCloseBulkSubscribedConfirm}
                />
            </Drawer>
        );
    },
);

const mapDispatchToProps = (dispatch) => ({
    setActiveEntity: bindActionCreators(EntityListSelectActions, dispatch).setActive,
    resetListSelect: bindActionCreators(EntityListSelectActions, dispatch).resetListSelect,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
    hideFilters: bindActionCreators(EntityFiltersActions, dispatch).hideFilters,
});

export default connect(null, mapDispatchToProps)(Subscribers);
